/**
 * @geenius/errors — Structured error handling
 *
 * Provides a hierarchy of typed errors for different error scenarios.
 * Enables consistent error handling and formatting across the Geenius ecosystem.
 */

/**
 * Defines the machine-readable error taxonomy shared across Geenius packages.
 * Use these values when errors need stable branching, serialization, or HTTP
 * status mapping instead of matching on human-facing messages.
 *
 * @example
 * if (isErrorCode(error, ErrorCode.RATE_LIMIT)) {
 *   retryAfterBackoff()
 * }
 */
export enum ErrorCode {
  // Generic
  UNKNOWN = 'UNKNOWN',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  CONFLICT = 'CONFLICT',
  INTERNAL = 'INTERNAL',

  // Auth-specific
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  INVALID_TOKEN = 'INVALID_TOKEN',

  // Database
  DATABASE_ERROR = 'DATABASE_ERROR',
  CONSTRAINT_VIOLATION = 'CONSTRAINT_VIOLATION',

  // Network
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',

  // Rate limiting
  RATE_LIMIT = 'RATE_LIMIT',

  // Internal
  INTERNAL_ERROR = 'INTERNAL_ERROR',

  // Configuration
  INVALID_CONFIG = 'INVALID_CONFIG',
  MISSING_CONFIG = 'MISSING_CONFIG',
}

const ERROR_STATUS_CODES = {
  [ErrorCode.UNKNOWN]: 500,
  [ErrorCode.VALIDATION]: 400,
  [ErrorCode.NOT_FOUND]: 404,
  [ErrorCode.UNAUTHORIZED]: 401,
  [ErrorCode.FORBIDDEN]: 403,
  [ErrorCode.CONFLICT]: 409,
  [ErrorCode.INTERNAL]: 500,
  [ErrorCode.INVALID_CREDENTIALS]: 401,
  [ErrorCode.SESSION_EXPIRED]: 401,
  [ErrorCode.MFA_REQUIRED]: 403,
  [ErrorCode.INVALID_TOKEN]: 401,
  [ErrorCode.DATABASE_ERROR]: 500,
  [ErrorCode.CONSTRAINT_VIOLATION]: 409,
  [ErrorCode.NETWORK_ERROR]: 502,
  [ErrorCode.TIMEOUT]: 504,
  [ErrorCode.RATE_LIMIT]: 429,
  [ErrorCode.INTERNAL_ERROR]: 500,
  [ErrorCode.INVALID_CONFIG]: 500,
  [ErrorCode.MISSING_CONFIG]: 500,
} as const satisfies Record<ErrorCode, number>

function buildStatusErrorCodes(): ReadonlyMap<number, readonly ErrorCode[]> {
  const statusCodes = new Map<number, ErrorCode[]>()

  for (const code of Object.values(ErrorCode)) {
    const statusCode = ERROR_STATUS_CODES[code]
    const codes = statusCodes.get(statusCode) ?? []
    codes.push(code)
    statusCodes.set(statusCode, codes)
  }

  return new Map(
    Array.from(statusCodes.entries(), ([statusCode, codes]) => [
      statusCode,
      Object.freeze([...codes]),
    ])
  )
}

const STATUS_ERROR_CODES = buildStatusErrorCodes()

const AUTH_ERROR_CODES = [
  ErrorCode.INVALID_CREDENTIALS,
  ErrorCode.SESSION_EXPIRED,
  ErrorCode.MFA_REQUIRED,
  ErrorCode.INVALID_TOKEN,
  ErrorCode.UNAUTHORIZED,
  ErrorCode.FORBIDDEN,
] as const

type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number]

interface AuthErrorOptions extends Omit<GeeniusErrorOptions, 'code' | 'statusCode'> {
  /** Auth-specific error code. */
  code?: AuthErrorCode
}

/**
 * Configures structured Geenius errors with both user-safe messages and
 * machine-readable metadata for logs, API responses, and UI recovery states.
 *
 * @example
 * const options: GeeniusErrorOptions = {
 *   code: ErrorCode.VALIDATION,
 *   message: 'Invalid email format',
 *   statusCode: 400,
 *   context: { field: 'email' },
 * }
 */
export interface GeeniusErrorOptions {
  /** Error code for classification */
  code?: ErrorCode
  /** User-friendly message */
  message: string
  /** Technical details */
  cause?: unknown
  /** HTTP status code equivalent */
  statusCode?: number
  /** Contextual data */
  context?: Record<string, unknown>
  /** Whether error should be logged */
  log?: boolean
}

/**
 * Identifies the closest concrete error class when a Geenius error crosses a
 * process, worker, or network boundary.
 *
 * @example
 * const kind: GeeniusErrorKind = 'RateLimitError'
 */
export type GeeniusErrorKind =
  | 'GeeniusError'
  | 'ValidationError'
  | 'AuthError'
  | 'NotFoundError'
  | 'ConflictError'
  | 'RateLimitError'
  | 'InternalServerError'
  | 'UnauthorizedError'

/**
 * Carries Geenius errors across network, worker, and storage boundaries without
 * leaking native `Error` instances or non-cloneable context values.
 *
 * @example
 * const serialized: SerializedGeeniusError = serializeError(error)
 */
export interface SerializedGeeniusError {
  [key: string]: unknown
  __isGeeniusError: true
  name: string
  kind: GeeniusErrorKind
  code: ErrorCode
  message: string
  statusCode: number
  context: Record<string, unknown>
  cause?: Record<string, unknown>
}

/**
 * Accepts either a live Geenius error or its serialized snapshot so guards and
 * formatters work before and after transport.
 *
 * @example
 * const value: GeeniusErrorLike = serializeError(new GeeniusError({ message: 'Failed' }))
 */
export type GeeniusErrorLike = GeeniusError | SerializedGeeniusError
/**
 * Represents the success-or-error tuple returned by safe wrappers. Use this
 * when callers prefer explicit branching over exceptions.
 *
 * @typeParam T - Successful result value type.
 * @typeParam TError - Geenius error type returned on failure.
 *
 * @example
 * const tuple: ErrorTuple<string> = ['ok', null]
 */
export type ErrorTuple<T, TError extends GeeniusError = GeeniusError> =
  | [T, null]
  | [null, TError]
/**
 * Converts unknown failures into native `Error` instances before the helpers
 * normalize them into `GeeniusError` values.
 *
 * @typeParam TError - Native error type produced by the handler.
 * @param error - Unknown failure caught by a safe wrapper.
 * @returns A native error that will be normalized into a Geenius error.
 *
 * @example
 * const handler: ErrorHandler<InternalServerError> = (error) =>
 *   new InternalServerError({ message: 'Operation failed', cause: error })
 */
export type ErrorHandler<TError extends Error = Error> = (error: unknown) => TError

/**
 * Controls how much diagnostic data is included when unknown thrown values are
 * normalized for logs or transport.
 *
 * @example
 * const options: FormatErrorOptions = { includeStack: process.env.NODE_ENV !== 'production' }
 */
export interface FormatErrorOptions {
  /** Include native Error.stack in formatted output. Defaults to false. */
  includeStack?: boolean
}

/**
 * Describes the JSON-safe shape returned by `formatError` for logs, API
 * responses, and reporting pipelines.
 *
 * @example
 * const formatted: FormattedError = formatError(new Error('Failed'))
 */
export interface FormattedError extends Record<string, unknown> {
  /** Native error name when available. */
  name?: string
  /** User-safe error message. */
  message: string
  /** Geenius error code classification. */
  code: ErrorCode
  /** HTTP status code equivalent. */
  statusCode: number
  /** Contextual metadata associated with the error. */
  context: Record<string, unknown>
  /** Native stack trace when explicitly requested. */
  stack?: string
}

/**
 * Resolves a canonical Geenius code to its default HTTP status for API
 * adapters that need stable response mapping.
 *
 * @param code - Geenius error code to resolve.
 * @returns The default HTTP status code associated with the code.
 *
 * @example
 * const statusCode = getStatusCodeForErrorCode(ErrorCode.NOT_FOUND)
 */
export function getStatusCodeForErrorCode(code: ErrorCode): number {
  return ERROR_STATUS_CODES[code]
}

/**
 * Lists every Geenius code that shares an HTTP status so consumers can build
 * grouped response handling or documentation tables.
 *
 * @param statusCode - HTTP status code to reverse-map.
 * @returns All known Geenius error codes associated with the status.
 *
 * @example
 * const unauthorizedCodes = getErrorCodesForStatusCode(401)
 */
export function getErrorCodesForStatusCode(statusCode: number): readonly ErrorCode[] {
  return STATUS_ERROR_CODES.get(statusCode) ?? []
}

/**
 * Narrows values that are known concrete Geenius errors after construction or
 * deserialization.
 *
 * @example
 * const known: KnownGeeniusError = new UnauthorizedError({ message: 'Login required' })
 */
export type KnownGeeniusError =
  | GeeniusError
  | ValidationError
  | AuthError
  | NotFoundError
  | ConflictError
  | RateLimitError
  | InternalServerError
  | UnauthorizedError

type NormalizedHandlerError<TError extends Error> = [TError] extends [never]
  ? GeeniusError
  : [TError] extends [GeeniusError]
    ? TError
    : GeeniusError
type StructuredCloneFunction = <T>(value: T) => T
const CIRCULAR_REFERENCE_MARKER = '[Circular]'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isKnownErrorCode(value: unknown): value is ErrorCode {
  return Object.values(ErrorCode).includes(value as ErrorCode)
}

function isAuthErrorCode(value: unknown): value is AuthErrorCode {
  return AUTH_ERROR_CODES.includes(value as AuthErrorCode)
}

function isKnownGeeniusErrorKind(value: unknown): value is GeeniusErrorKind {
  return [
    'GeeniusError',
    'ValidationError',
    'AuthError',
    'NotFoundError',
    'ConflictError',
    'RateLimitError',
    'InternalServerError',
    'UnauthorizedError',
  ].includes(String(value))
}

function isSerializedCodeConsistentWithKind(
  kind: GeeniusErrorKind,
  code: ErrorCode
): boolean {
  switch (kind) {
    case 'GeeniusError':
      return true
    case 'ValidationError':
      return code === ErrorCode.VALIDATION
    case 'AuthError':
      return isAuthErrorCode(code)
    case 'NotFoundError':
      return code === ErrorCode.NOT_FOUND
    case 'ConflictError':
      return code === ErrorCode.CONFLICT
    case 'RateLimitError':
      return code === ErrorCode.RATE_LIMIT
    case 'InternalServerError':
      return code === ErrorCode.INTERNAL_ERROR
    case 'UnauthorizedError':
      return code === ErrorCode.UNAUTHORIZED
  }
}

function isSerializedStatusConsistentWithKind(
  kind: GeeniusErrorKind,
  code: ErrorCode,
  statusCode: number
): boolean {
  if (!isErrorStatusCode(statusCode)) {
    return false
  }

  return kind === 'GeeniusError' || ERROR_STATUS_CODES[code] === statusCode
}

function cloneValueFallback(
  value: unknown,
  seen = new WeakMap<object, unknown>()
): unknown {
  if (value instanceof Date) {
    return new Date(value)
  }

  if (value instanceof RegExp) {
    return new RegExp(value)
  }

  if (value instanceof Error) {
    return formatNativeError(value)
  }

  if (value instanceof Map) {
    const existing = seen.get(value)
    if (existing) {
      return existing
    }

    const cloned = new Map<unknown, unknown>()
    seen.set(value, cloned)
    for (const [key, item] of value.entries()) {
      cloned.set(cloneValueFallback(key, seen), cloneValueFallback(item, seen))
    }
    return cloned
  }

  if (value instanceof Set) {
    const existing = seen.get(value)
    if (existing) {
      return existing
    }

    const cloned = new Set<unknown>()
    seen.set(value, cloned)
    for (const item of value.values()) {
      cloned.add(cloneValueFallback(item, seen))
    }
    return cloned
  }

  if (Array.isArray(value)) {
    const existing = seen.get(value)
    if (existing) {
      return existing
    }

    const cloned: unknown[] = []
    seen.set(value, cloned)
    for (const item of value) {
      cloned.push(cloneValueFallback(item, seen))
    }
    return cloned
  }

  if (isRecord(value)) {
    const existing = seen.get(value)
    if (existing) {
      return existing
    }

    const cloned: Record<string, unknown> = {}
    seen.set(value, cloned)
    for (const [key, item] of Object.entries(value)) {
      cloned[key] = cloneValueFallback(item, seen)
    }
    return cloned
  }

  return value
}

function cloneValue(value: unknown): unknown {
  const structuredClone = (globalThis as {
    structuredClone?: StructuredCloneFunction
  }).structuredClone

  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value)
    } catch {
      return cloneValueFallback(value)
    }
  }

  return cloneValueFallback(value)
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  const cloned = cloneValue(value)
  return isRecord(cloned) ? cloned : {}
}

function toJsonSafeValue(
  value: unknown,
  ancestors = new WeakSet<object>()
): unknown {
  if (typeof value !== 'object' || value === null) {
    if (typeof value === 'bigint') {
      return value.toString()
    }

    if (typeof value === 'function') {
      return '[Function]'
    }

    if (typeof value === 'symbol') {
      return String(value)
    }

    return value
  }

  if (ancestors.has(value)) {
    return CIRCULAR_REFERENCE_MARKER
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (value instanceof RegExp) {
    return value.toString()
  }

  if (value instanceof Error) {
    return formatNativeError(value)
  }

  ancestors.add(value)

  if (Array.isArray(value)) {
    const result = value.map((item) => toJsonSafeValue(item, ancestors))
    ancestors.delete(value)
    return result
  }

  if (value instanceof Map) {
    const result: Record<string, unknown> = {}
    for (const [key, item] of value.entries()) {
      result[String(key)] = toJsonSafeValue(item, ancestors)
    }
    ancestors.delete(value)
    return result
  }

  if (value instanceof Set) {
    const result = Array.from(value, (item) => toJsonSafeValue(item, ancestors))
    ancestors.delete(value)
    return result
  }

  const result: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) {
    result[key] = toJsonSafeValue(item, ancestors)
  }
  ancestors.delete(value)
  return result
}

function toJsonSafeRecord(value: Record<string, unknown>): Record<string, unknown> {
  const safeValue = toJsonSafeValue(value)
  return isRecord(safeValue) ? safeValue : {}
}

function getFormattedMessage(value: unknown): string {
  if (isRecord(value) && typeof value.message === 'string') {
    return value.message
  }

  return String(value)
}

function getFormattedCode(value: unknown): ErrorCode {
  return isKnownErrorCode(value) ? value : ErrorCode.UNKNOWN
}

function isErrorStatusCode(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 400 &&
    value <= 599
  )
}

function getFormattedStatusCode(value: unknown, code: ErrorCode): number {
  if (isErrorStatusCode(value)) {
    return value
  }

  return ERROR_STATUS_CODES[code]
}

function getFormattedContext(value: unknown): Record<string, unknown> {
  return isRecord(value) ? toJsonSafeRecord(value) : {}
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    isRecord(value) &&
    typeof value.then === 'function'
  )
}

function toGeeniusError(error: unknown): GeeniusError {
  if (error instanceof GeeniusError) {
    return error
  }

  if (isSerializedGeeniusError(error)) {
    return deserializeError(error)
  }

  if (error instanceof Error) {
    return new GeeniusError({
      message: error.message,
      cause: error,
    })
  }

  if (isRecord(error)) {
    const code = getFormattedCode(error.code)

    return new GeeniusError({
      message: getFormattedMessage(error),
      code,
      statusCode: getFormattedStatusCode(error.statusCode, code),
      context: getFormattedContext(error.context),
      cause: error.cause,
    })
  }

  return new GeeniusError({
    message: String(error),
  })
}

function handleError<TError extends Error = Error>(
  error: unknown,
  handler?: ErrorHandler<TError>
): NormalizedHandlerError<TError> {
  if (!handler) {
    return toGeeniusError(error) as NormalizedHandlerError<TError>
  }

  try {
    return toGeeniusError(handler(error)) as NormalizedHandlerError<TError>
  } catch (handlerError) {
    return toGeeniusError(handlerError) as NormalizedHandlerError<TError>
  }
}

function formatNativeError(
  error: Error,
  options: FormatErrorOptions = {}
): FormattedError {
  return {
    name: error.name,
    message: error.message,
    code: ErrorCode.UNKNOWN,
    statusCode: ERROR_STATUS_CODES[ErrorCode.UNKNOWN],
    context: {},
    ...(options.includeStack && typeof error.stack === 'string'
      ? { stack: error.stack }
      : {}),
  }
}

function formatUnknownRecord(
  error: Record<string, unknown>,
  options: FormatErrorOptions = {}
): FormattedError {
  const code = getFormattedCode(error.code)
  const safeRecord = toJsonSafeRecord(error)

  if (!options.includeStack || typeof safeRecord.stack !== 'string') {
    delete safeRecord.stack
  }

  return {
    ...safeRecord,
    message: getFormattedMessage(error),
    code,
    statusCode: getFormattedStatusCode(error.statusCode, code),
    context: getFormattedContext(error.context),
  }
}

function isSerializedGeeniusError(error: unknown): error is SerializedGeeniusError {
  if (!isRecord(error)) {
    return false
  }

  if (
    !isKnownGeeniusErrorKind(error.kind) ||
    !isKnownErrorCode(error.code) ||
    !isErrorStatusCode(error.statusCode)
  ) {
    return false
  }

  return (
    error.__isGeeniusError === true &&
    error.name === error.kind &&
    typeof error.message === 'string' &&
    isRecord(error.context) &&
    isSerializedCodeConsistentWithKind(error.kind, error.code) &&
    isSerializedStatusConsistentWithKind(error.kind, error.code, error.statusCode)
  )
}

/**
 * Provides the base structured error for Geenius packages. Use this when a
 * package needs a typed error with code, status, context, and safe JSON output
 * but no narrower subclass applies.
 *
 * @param options - Structured error configuration used to set message, code,
 * status, context, cause, and logging intent.
 * @returns A live `GeeniusError` instance with a safe serialization contract.
 *
 * @example
 * ```ts
 * throw new GeeniusError({
 *   code: ErrorCode.VALIDATION,
 *   message: 'Invalid email format',
 *   statusCode: 400,
 *   context: { field: 'email' }
 * })
 * ```
 */
export class GeeniusError extends Error {
  readonly kind: GeeniusErrorKind = 'GeeniusError'
  readonly code: ErrorCode
  readonly statusCode: number
  readonly context: Record<string, unknown>
  readonly cause?: unknown
  readonly log: boolean

  /**
   * Creates a structured error that can be safely formatted and serialized.
   *
   * @param options - Message, classification, HTTP status, cause, and context.
   *
   * @example
   * const error = new GeeniusError({ message: 'Operation failed' })
   */
  constructor(options: GeeniusErrorOptions) {
    super(options.message)
    this.name = 'GeeniusError'
    this.code = isKnownErrorCode(options.code) ? options.code : ErrorCode.UNKNOWN
    this.statusCode = isErrorStatusCode(options.statusCode)
      ? options.statusCode
      : this.getDefaultStatusCode(this.code)
    this.context = options.context ? cloneRecord(options.context) : {}
    this.cause = options.cause
    this.log = options.log !== false

    // Maintain proper prototype chain
    Object.setPrototypeOf(this, GeeniusError.prototype)
  }

  private getDefaultStatusCode(code: ErrorCode): number {
    return ERROR_STATUS_CODES[code]
  }

  /**
   * Converts the live error into the stable payload used for transport and
   * storage boundaries.
   *
   * @returns A JSON-safe Geenius error snapshot.
   *
   * @example
   * const payload = new GeeniusError({ message: 'Failed' }).toJSON()
   */
  toJSON(): SerializedGeeniusError {
    return {
      __isGeeniusError: true,
      name: this.name,
      kind: this.kind,
      code: this.code,
      message: this.message,
      statusCode: this.statusCode,
      context: toJsonSafeRecord(this.context),
      ...(this.cause ? { cause: formatError(this.cause) } : {}),
    }
  }

  /**
   * Produces a compact diagnostic string for logs and CLI output.
   *
   * @returns A string containing the error name, code, and message.
   *
   * @example
   * String(new GeeniusError({ message: 'Failed' }))
   */
  toString(): string {
    return `${this.name} [${this.code}]: ${this.message}`
  }
}

/**
 * Represents input, schema, or form validation failures. Use it when callers
 * can recover by correcting submitted data.
 *
 * @param options - Message, context, cause, and logging intent for the
 * validation failure.
 * @returns A `ValidationError` mapped to `ErrorCode.VALIDATION` and HTTP 400.
 *
 * @example
 * ```ts
 * throw new ValidationError({
 *   message: 'Invalid input',
 *   context: { errors: { email: 'Invalid format' } }
 * })
 * ```
 */
export class ValidationError extends GeeniusError {
  override readonly kind = 'ValidationError'

  /**
   * Creates a validation error with the canonical validation code and status.
   *
   * @param options - Message, context, cause, and logging intent.
   *
   * @example
   * const error = new ValidationError({ message: 'Invalid input' })
   */
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.VALIDATION,
      statusCode: 400,
    })
    this.name = 'ValidationError'
    Object.setPrototypeOf(this, ValidationError.prototype)
  }
}

/**
 * Represents authentication and authorization-flow failures that need a more
 * specific auth code than the base error.
 *
 * @param options - Auth failure details and optional auth-specific code.
 * @returns An `AuthError` mapped to the matching auth HTTP status.
 *
 * @example
 * const error = new AuthError({
 *   code: ErrorCode.SESSION_EXPIRED,
 *   message: 'Session expired',
 * })
 */
export class AuthError extends GeeniusError {
  override readonly kind = 'AuthError'

  /**
   * Creates an auth error with a validated auth-specific code.
   *
   * @param options - Auth-specific message, code, context, cause, and log flag.
   *
   * @example
   * const error = new AuthError({ message: 'Invalid credentials' })
   */
  constructor(options: AuthErrorOptions) {
    const code = isAuthErrorCode(options.code)
      ? options.code
      : ErrorCode.INVALID_CREDENTIALS

    super({
      ...options,
      code,
      statusCode: ERROR_STATUS_CODES[code],
    })
    this.name = 'AuthError'
    Object.setPrototypeOf(this, AuthError.prototype)
  }
}

/**
 * Represents a missing resource. Use it when a lookup completed but no matching
 * entity exists.
 *
 * @param options - Message, context, cause, and logging intent for the miss.
 * @returns A `NotFoundError` mapped to `ErrorCode.NOT_FOUND` and HTTP 404.
 *
 * @example
 * const error = new NotFoundError({ message: 'Project not found' })
 */
export class NotFoundError extends GeeniusError {
  override readonly kind = 'NotFoundError'

  /**
   * Creates a not-found error with the canonical not-found code and status.
   *
   * @param options - Message, context, cause, and logging intent.
   *
   * @example
   * const error = new NotFoundError({ message: 'User not found' })
   */
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.NOT_FOUND,
      statusCode: 404,
    })
    this.name = 'NotFoundError'
    Object.setPrototypeOf(this, NotFoundError.prototype)
  }
}

/**
 * Represents duplicate, stale-write, or conflicting-state failures. Use it
 * when retrying requires changing the requested state.
 *
 * @param options - Message, context, cause, and logging intent for the conflict.
 * @returns A `ConflictError` mapped to `ErrorCode.CONFLICT` and HTTP 409.
 *
 * @example
 * const error = new ConflictError({ message: 'Email already exists' })
 */
export class ConflictError extends GeeniusError {
  override readonly kind = 'ConflictError'

  /**
   * Creates a conflict error with the canonical conflict code and status.
   *
   * @param options - Message, context, cause, and logging intent.
   *
   * @example
   * const error = new ConflictError({ message: 'Duplicate key' })
   */
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.CONFLICT,
      statusCode: 409,
    })
    this.name = 'ConflictError'
    Object.setPrototypeOf(this, ConflictError.prototype)
  }
}

/**
 * Represents throttling and quota failures. Use it when callers should slow
 * down or retry after backoff.
 *
 * @param options - Message, context, cause, and logging intent for throttling.
 * @returns A `RateLimitError` mapped to `ErrorCode.RATE_LIMIT` and HTTP 429.
 *
 * @example
 * const error = new RateLimitError({ message: 'Too many requests' })
 */
export class RateLimitError extends GeeniusError {
  override readonly kind = 'RateLimitError'

  /**
   * Creates a rate-limit error with the canonical rate-limit code and status.
   *
   * @param options - Message, context, cause, and logging intent.
   *
   * @example
   * const error = new RateLimitError({ message: 'Quota exceeded' })
   */
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.RATE_LIMIT,
      statusCode: 429,
    })
    this.name = 'RateLimitError'
    Object.setPrototypeOf(this, RateLimitError.prototype)
  }
}

/**
 * Represents unexpected internal failures that should not expose implementation
 * details to external consumers.
 *
 * @param options - Message, context, cause, and logging intent for the failure.
 * @returns An `InternalServerError` mapped to `ErrorCode.INTERNAL_ERROR` and
 * HTTP 500.
 *
 * @example
 * const error = new InternalServerError({ message: 'Failed to save record' })
 */
export class InternalServerError extends GeeniusError {
  override readonly kind = 'InternalServerError'

  /**
   * Creates an internal server error with the canonical internal code/status.
   *
   * @param options - Message, context, cause, and logging intent.
   *
   * @example
   * const error = new InternalServerError({ message: 'Unexpected failure' })
   */
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.INTERNAL_ERROR,
      statusCode: 500,
    })
    this.name = 'InternalServerError'
    Object.setPrototypeOf(this, InternalServerError.prototype)
  }
}

/**
 * Represents missing or invalid credentials. Use it for authentication gates
 * where the user must provide or refresh credentials.
 *
 * @param options - Message, context, cause, and logging intent for the auth gap.
 * @returns An `UnauthorizedError` mapped to `ErrorCode.UNAUTHORIZED` and HTTP
 * 401.
 *
 * @example
 * const error = new UnauthorizedError({ message: 'Login required' })
 */
export class UnauthorizedError extends GeeniusError {
  override readonly kind = 'UnauthorizedError'

  /**
   * Creates an unauthorized error with the canonical unauthorized code/status.
   *
   * @param options - Message, context, cause, and logging intent.
   *
   * @example
   * const error = new UnauthorizedError({ message: 'Missing session' })
   */
  constructor(options: Omit<GeeniusErrorOptions, 'code' | 'statusCode'>) {
    super({
      ...options,
      code: ErrorCode.UNAUTHORIZED,
      statusCode: 401,
    })
    this.name = 'UnauthorizedError'
    Object.setPrototypeOf(this, UnauthorizedError.prototype)
  }
}

/**
 * Normalizes any thrown value into a JSON-safe error record. Use this at
 * logging, telemetry, and API boundaries where callers may throw strings,
 * native errors, Geenius errors, or plain objects.
 *
 * @param error - Unknown thrown value to normalize.
 * @param options - Formatting controls, including optional stack inclusion.
 * @returns A structured error record with message, code, status, and context.
 *
 * @example
 * const formatted = formatError(error, { includeStack: shouldExposeStack })
 */
export function formatError(
  error: unknown,
  options: FormatErrorOptions = {}
): FormattedError {
  if (error instanceof GeeniusError) {
    return {
      ...error.toJSON(),
      ...(options.includeStack && typeof error.stack === 'string'
        ? { stack: error.stack }
        : {}),
    }
  }

  if (error instanceof Error) {
    return formatNativeError(error, options)
  }

  if (typeof error === 'string') {
    return {
      message: error,
      code: ErrorCode.UNKNOWN,
      statusCode: ERROR_STATUS_CODES[ErrorCode.UNKNOWN],
      context: {},
    }
  }

  if (isRecord(error)) {
    return formatUnknownRecord(error, options)
  }

  return {
    message: String(error),
    code: ErrorCode.UNKNOWN,
    statusCode: ERROR_STATUS_CODES[ErrorCode.UNKNOWN],
    context: {},
  }
}

/**
 * Checks whether a value is either a live Geenius error or a serialized
 * Geenius error snapshot. Use this before branching on `code`, `statusCode`,
 * or `kind` across process boundaries.
 *
 * @param error - Unknown value to inspect.
 * @returns `true` when the value satisfies the Geenius error contract.
 *
 * @example
 * if (isGeeniusError(error)) {
 *   console.error(error.code)
 * }
 */
export function isGeeniusError(error: unknown): error is GeeniusErrorLike {
  return error instanceof GeeniusError || isSerializedGeeniusError(error)
}

/**
 * Checks whether a live or serialized Geenius error carries a specific code.
 * Use this for stable branching instead of matching on messages.
 *
 * @param error - Unknown value to inspect.
 * @param code - Error code to match.
 * @returns `true` when the value is a Geenius error with the requested code.
 *
 * @example
 * const retry = isErrorCode(error, ErrorCode.RATE_LIMIT)
 */
export function isErrorCode(error: unknown, code: ErrorCode): boolean {
  return isGeeniusError(error) && error.code === code
}

/**
 * Serialize any thrown value into the stable Geenius error payload used across
 * network and worker boundaries.
 *
 * @param error - Unknown thrown value or existing Geenius error.
 * @returns A serialized Geenius error payload safe for JSON and structured
 * clone boundaries.
 *
 * @example
 * const payload = serializeError(new RateLimitError({ message: 'Slow down' }))
 */
export function serializeError(error: unknown): SerializedGeeniusError {
  return toGeeniusError(error).toJSON()
}

/**
 * Rehydrate a serialized Geenius error payload into the closest matching error
 * subclass. Unknown payloads are normalized into a base `GeeniusError`.
 *
 * @param error - Serialized Geenius error snapshot or unknown fallback value.
 * @returns A live Geenius error instance, using the closest concrete subclass
 * when the serialized kind is valid.
 *
 * @example
 * const error = deserializeError(payload)
 */
export function deserializeError(error: unknown): GeeniusError {
  if (!isSerializedGeeniusError(error)) {
    return toGeeniusError(error)
  }

  switch (error.kind) {
    case 'ValidationError':
      return new ValidationError({
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    case 'AuthError': {
      const authCode = isAuthErrorCode(error.code)
        ? error.code
        : ErrorCode.INVALID_CREDENTIALS
      return new AuthError({
        code: authCode,
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    }
    case 'NotFoundError':
      return new NotFoundError({
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    case 'ConflictError':
      return new ConflictError({
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    case 'RateLimitError':
      return new RateLimitError({
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    case 'InternalServerError':
      return new InternalServerError({
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    case 'UnauthorizedError':
      return new UnauthorizedError({
        message: error.message,
        context: error.context,
        cause: error.cause,
      })
    default:
      return new GeeniusError({
        code: error.code,
        message: error.message,
        statusCode: error.statusCode,
        context: error.context,
        cause: error.cause,
      })
  }
}

/**
 * Wraps an operation and returns a Go-style result tuple.
 *
 * @typeParam T - Operation result type.
 * @typeParam TError - Optional native error type produced by the handler.
 * @param operation - Promise or function to execute inside the safe boundary.
 * @param handler - Optional transformation for unknown failures before they are
 * normalized to Geenius errors.
 * @returns A tuple containing either `[value, null]` or `[null, error]`.
 *
 * @example
 * ```ts
 * const [result, error] = await catchError(
 *   fetchApi(),
 *   (err) => new GeeniusError({
 *     code: ErrorCode.NETWORK_ERROR,
 *     message: 'API request failed',
 *     cause: err
 *   })
 * )
 * ```
 */
export async function catchError<T, TError extends Error = Error>(
  operation: Promise<T> | (() => T | Promise<T>),
  handler?: ErrorHandler<TError>
): Promise<ErrorTuple<Awaited<T>, NormalizedHandlerError<TError>>> {
  try {
    const result =
      typeof operation === 'function' ? operation() : operation
    return [
      await result,
      null,
    ] as ErrorTuple<Awaited<T>, NormalizedHandlerError<TError>>
  } catch (error) {
    return [null, handleError(error, handler)]
  }
}

type SafeReturn<TResult, TError extends GeeniusError = GeeniusError> =
  TResult extends PromiseLike<infer T>
    ? Promise<ErrorTuple<T, TError>>
    : ErrorTuple<TResult, TError>

/**
 * Creates a safe function that returns Go-style result tuples.
 *
 * @typeParam TArgs - Original function argument tuple.
 * @typeParam TResult - Original function return type.
 * @typeParam TError - Optional native error type produced by the handler.
 * @param fn - Function to wrap while preserving its argument list.
 * @param handler - Optional transformation for unknown failures before they are
 * normalized to Geenius errors.
 * @returns A function with the same parameters that returns an error tuple.
 *
 * @example
 * ```ts
 * const safeOp = makeSafe(
 *   (id: string) => db.user.find(id),
 *   (err) => new NotFoundError({ message: 'User not found', cause: err })
 * )
 * ```
 */
export function makeSafe<
  TArgs extends unknown[],
  TResult,
  TError extends Error = Error,
>(
  fn: (...args: TArgs) => TResult,
  handler?: ErrorHandler<TError>
): (...args: TArgs) => SafeReturn<TResult, NormalizedHandlerError<TError>> {
  const safeFn = (
    ...args: TArgs
  ): SafeReturn<TResult, NormalizedHandlerError<TError>> => {
    try {
      const result = fn(...args)
      if (isPromiseLike(result)) {
        return Promise.resolve(result).then(
          (value) => [
            value,
            null,
          ] as ErrorTuple<Awaited<TResult>, NormalizedHandlerError<TError>>,
          (error: unknown) => [
            null,
            handleError(error, handler),
          ] as ErrorTuple<Awaited<TResult>, NormalizedHandlerError<TError>>
        ) as SafeReturn<TResult, NormalizedHandlerError<TError>>
      }
      return [result, null] as SafeReturn<
        TResult,
        NormalizedHandlerError<TError>
      >
    } catch (error) {
      return [null, handleError(error, handler)] as SafeReturn<
        TResult,
        NormalizedHandlerError<TError>
      >
    }
  }

  // Preserve the original function signature shape for higher-order wrappers.
  return safeFn
}
